import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ProfilesProvider } from "@/lib/profiles";
import Login from "@/pages/Login";
import PendingApproval from "@/pages/PendingApproval";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Invoices from "@/pages/Invoices";
import InvoiceEditor from "@/pages/InvoiceEditor";
import Quotations from "@/pages/Quotations";
import QuotationEditor from "@/pages/QuotationEditor";
import Finance from "@/pages/Finance";
import Vendors from "@/pages/Vendors";
import Clients from "@/pages/Clients";
import Profiles from "@/pages/Profiles";
import Admin from "@/pages/Admin";
import { Toaster } from "@/components/ui/sonner";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-neutral-500 font-mono text-xs uppercase tracking-[0.3em]">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin" && !user.is_approved) return <PendingApproval />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/invoices/new" element={<InvoiceEditor />} />
        <Route path="/invoices/:id" element={<InvoiceEditor />} />
        <Route path="/quotations" element={<Quotations />} />
        <Route path="/quotations/new" element={<QuotationEditor />} />
        <Route path="/quotations/:id" element={<QuotationEditor />} />
        <Route path="/finance" element={<Finance />} />
        <Route path="/vendors" element={<Vendors />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/profiles" element={<Profiles />} />
        <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ProfilesProvider>
          <AppRouter />
          <Toaster theme="dark" position="top-right" />
        </ProfilesProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
